import useExportEquipmentSurvey from "ApiLayer/Equipment/queryExportEquipmentSurvey";
import useExportModeledUtilityData from "ApiLayer/Building/queryExportModeledUtilityData";
import { Button } from "components/atoms/Button";
import { ImportIcon, RedirectIcon, UploadIcon } from "components/atoms/Icon";
import LoadingSpinner from "components/atoms/LoadingSpinner/LoadingSpinner";
import EquipmentUploadModal from "components/organisms/Modals/EquipmentUploadModal/EquipmentUploadModal";
import { useBuildingModelUid } from "hooks/useBuildingModelUid";
import { usePermissions } from "hooks/usePermissions";
import { useQueryOnStatusUpdate } from "pages/BuildingPage/BuildingSidebar/BuildingEnergy.hooks";
import { useSubmitEquipmentData } from "queries/Equipment/useSubmitEquipmentSurvey";
import React, { useState } from "react";
import { Navigate } from "react-router-dom";
import { UserPermissions } from "utils/enums";
import styles from "./Sandbox.module.scss";

interface SandboxLayoutProps {
    children: React.ReactNode;
    title?: string;
}

function BuildingSandboxLayout({ children, title = "🧪 Building Sandbox" }: SandboxLayoutProps) {
    const { hasPermission, isFetching } = usePermissions();
    const buildingModelUid = useBuildingModelUid();
    const { mutate: downloadEquipmentCSV, isLoading } = useExportEquipmentSurvey(
        buildingModelUid,
        "equipment_survey.csv"
    );
    const { mutate: downloadModeledUtilityData, isLoading: modeledUtilityIsLoading } =
        useExportModeledUtilityData(buildingModelUid);
    const { onSuccess, onError } = useQueryOnStatusUpdate(buildingModelUid);
    const { mutate: submitEquipmentSurvey, isLoading: submitEquipmentIsLoading } = useSubmitEquipmentData(
        buildingModelUid,
        onSuccess,
        onError
    );
    const [modalIsOpen, setModalIsOpen] = useState(false);

    if (isFetching) return <LoadingSpinner />;

    if (!hasPermission(UserPermissions.UseInternalTools)) {
        return <Navigate to="/" replace />;
    }

    return (
        <div className={styles["sandbox-container"]}>
            <div className={styles["sandbox-header"]}>
                <h1>{title}</h1>
                <div className={styles["button-container"]}>
                    <Button
                        type="secondary"
                        onClick={() => setModalIsOpen(true)}
                        icon={<UploadIcon />}
                        id="sandbox-button"
                        disabled={submitEquipmentIsLoading}
                    >
                        Upload equipment CSV
                    </Button>
                    <Button
                        type="secondary"
                        onClick={downloadEquipmentCSV}
                        icon={<ImportIcon />}
                        id="sandbox-button"
                        disabled={isLoading}
                    >
                        Download equipment CSV
                    </Button>
                    <Button
                        type="secondary"
                        onClick={downloadModeledUtilityData}
                        icon={<ImportIcon />}
                        id="sandbox-button"
                        disabled={modeledUtilityIsLoading}
                    >
                        Export modeled utility data
                    </Button>
                    <Button
                        type="secondary"
                        onClick={() =>
                            window.open(
                                "https://docs.google.com/spreadsheets/d/1nQW52ovGTIENGVgCuMAZbjkcTfTTlQpBsMmOVSAFoFg/",
                                "_blank"
                            )
                        }
                        icon={<RedirectIcon />}
                        id="sandbox-button"
                    >
                        CSV template
                    </Button>
                </div>
            </div>
            <div className={styles["sandbox-content"]}>{children}</div>
            <EquipmentUploadModal
                modalIsOpen={modalIsOpen}
                onCancel={() => setModalIsOpen(false)}
                submitEquipmentSurvey={submitEquipmentSurvey}
            />
        </div>
    );
}

export default BuildingSandboxLayout;
